/* @flow */
import { createStore, applyMiddleware, compose } from 'redux';
import type { Store } from 'redux';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import reduxPinkyMiddleware from 'redux-pinky';
import { createLogger } from 'redux-logger';

import keys from '../config/keys';
import reducers from './reducers';
import storageService from '../services/storage';

import type { Action, ReduxStore, ReduxState } from '../types';

const composeEnhancers =
  typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({})
    : compose;

export default (initialState?: ReduxState): ReduxStore => {
  const middlewares = [];

  // Async middleware
  middlewares.push(reduxPinkyMiddleware);

  // Redux logger
  if (keys.IS_ENV_DEVELOPMENT) {
    const logger = createLogger({ collapsed: true });
    middlewares.push(logger);
  }

  // Persist reducers
  let enhancedReducers = reducers;
  if (keys.REDUX_PERSIST_ACTIVE) {
    const persistConfig = {
      key: 'primary',
      storage: storage,
      whitelist: [],
      blacklist: ['app', 'cart', 'playerQueue', 'worklists'],
      transforms: [],
    };
    enhancedReducers = persistReducer(persistConfig, reducers);
  }

  // Create store
  const store: Store<ReduxState, Action> = createStore(
    enhancedReducers,
    composeEnhancers(applyMiddleware(...middlewares))
  );

  // Rehydrate reducers
  let persistor;
  if (keys.REDUX_PERSIST_ACTIVE) {
    // Ensure we are using the latest reducer version
    const previousReducerVersion = storageService.getFromStorage('reduxPersistReducerVersion');
    if (previousReducerVersion !== keys.REDUX_PERSIST_REDUCER_VERSION) {
      persistor = persistStore(store);
      persistor.purge();
      storageService.setInStorage('reduxPersistReducerVersion', keys.REDUX_PERSIST_REDUCER_VERSION);
    } else {
      persistor = persistStore(store);
    }
  }

  // Set hot reloading
  // $FlowFixMe
  if (keys.IS_ENV_DEVELOPMENT && module.hot) {
    //$FlowFixMe
    module.hot.accept('./reducers/index', () => {
      const nextReducer = require('./reducers');
      //$FlowFixMe
      store.replaceReducer(nextReducer);
    });
  }
  return {
    store,
    persistor,
  };
};
